<template>
  <div class="px-2 overflow-y-auto scrollbar h-full">
    <div class="store-locator">
      <div class="mb-3">
        <p class="text-sm" qaid="customization-lead-gen-send-order-text">{{ $t('projectOne.order.send') }}</p>
      </div>

      <div class="mb-3">
        <h3 class="heading heading--md" qaid="customization-lead-gen-model-name-header">{{ bikeData.modelName }}</h3>

        <div class="flex items-center justify-between mt-2">
          <p class="text-sm" qaid="customization-lead-gen-base-price-header">{{ $t('projectOne.order.basePrice') }}</p>
          <span class="text-sm" qaid="customization-lead-gen-base-price">{{ bikeData.defaultAdvertised }}</span>
        </div>

        <div class="flex items-center justify-between mt-2">
          <p class="text-sm" qaid="estimated-price-header">{{ $t('projectOne.order.estimatedPrice') }}</p>
          <span class="text-sm" qaid="estimated-price">{{ bikeData.calculatedAdvertised }}</span>
        </div>

        <p class="text-xs text-gray-60" qaid="customization-lead-gen-price-exclusion-text">
          {{ $t('projectOne.order.estimatedPrice.excluding') }}
        </p>
      </div>

      <h3 class="heading heading--md mb-3" qaid="customization-lead-gen-price-exclusion-order-text">
        {{ $t('projectOne.order.details') }}
      </h3>
      <div class="md:max-w-xs">
        <form @submit.prevent>
          <pdl-form-group
            v-model="customerName"
            type="text"
            name="name"
            :label="$t('requestReturnLabel.name')"
            disabled
            qaid="customization-lead-gen-name-input"
            :error="getErrorMessage('customerName')"
            required
          />

          <pdl-form-group
            v-model="customerEmail"
            type="email"
            name="email"
            disabled
            :label="$t('forgottenPwd.email')"
            qaid="customization-lead-gen-email-input"
            :error="getErrorMessage('customerEmail')"
            required
          />

          <pdl-form-group
            v-model="customerPhoneNumber"
            allowed-characters-regex="[\d-+. ()]"
            type="tel"
            :maxlength="20"
            name="customerPhoneNumber"
            :label="$t('contactUs.phone')"
            qaid="customization-lead-gen-phone-input"
            :error="getErrorMessage('customerPhoneNumber')"
            required
          />

          <pdl-form-group
            type="radio"
            name="customerContactMethod"
            :multi-items="preferredContactMethods"
            :label="$t('projectOne.order.contactMethod')"
            :required="true"
            qaid="customization-lead-gen-contact-method"
            :model-value="selectedContactMethod?.value"
            @change="handleContactMethodChange"
          />
        </form>
      </div>
      <div v-if="retailerLocation.id">
        <h3 class="heading heading--md mb-3" qaid="selected-retailer-header">
          {{ $t('projectOne.submit.selectedRetailer') }}
        </h3>
        <locator-store-card :value="retailerLocation" />
      </div>
      <div v-else>
        <form @submit.prevent>
          <h3 class="heading heading--md mb-3" qaid="customization-register-retailer-header">
            {{ $t('register.retailer.title') }}
          </h3>
          <div class="form-group">
            <label class="form-label" qaid="enter-city-label" for="search-location">
              <span>{{ $t('projectOne.order.location') }}</span>
              <span qaid="enter-city-label-asterick" class="text-red ml-25">*</span>
            </label>

            <div class="form-group--inline is-required">
              <mapbox-address-suggestions-input
                v-model="textSearchQuery"
                input-id="search-location"
                input-classes="form-control"
                container-classes="md:max-w-xs"
                input-qaid="customization-lead-gen-retailer-location-search-input"
                input-name="search-location"
                input-required="required"
                @input-updated="respondToInputChanges($event)"
              />
              <pdl-button
                primary
                form-inline
                class="ml-1"
                qaid="customization-lead-gen-location-search-button"
                @click.prevent="search"
              >
                <span qaid="customization-lead-gen-location-search-button-text">{{
                  $t('register.retailer.search')
                }}</span>
              </pdl-button>
            </div>
          </div>
        </form>
        <div class="locator-container">
          <locator-store-list
            :model-value="stores"
            :display-empty-message="hasSearched"
            :has-img-for-no-records="false"
            :is-loading="isLoading"
            :is-scroll-list="false"
            qaid="customization-store-locator-store-list"
            @store-clicked="setRetailerLocation"
          />
          <pagination-controls
            v-if="totalPages > 1 && stores.length"
            class="md:px-2"
            container-class="justify-between"
            :display-pages="false"
            :page-number="pagination.page"
            :page-count="totalPages"
            @page-change="pageChange"
            @prev-page="prevPage"
            @next-page="nextPage"
          >
            <template #previous>
              <span class="text-md ml-1">{{ $t('carousel.controls.previous') }}</span>
            </template>
            <template #next>
              <span class="text-md mr-1">{{ $t('carousel.controls.next') }}</span>
            </template>
          </pagination-controls>
        </div>
      </div>
      <div class="sticky bottom-0 bg-white pt-1 pb-2">
        <div class="buttons buttons--right py-3">
          <pdl-button
            primary
            qaid="customization-lead-gen-control-submit-btn"
            :disabled="!isSubmittable"
            @click="submit"
          >
            <span>{{ $t('contactUs.submit') }}</span>
          </pdl-button>
          <pdl-button
            v-if="retailerLocation.id"
            qaid="customization-lead-gen-select-new-retailer-button"
            secondary
            @click="clearRetailer"
          >
            <span qaid="customization-lead-gen-select-new-retailer-button-text">{{
              $t('projectOne.submit.clearRetailer')
            }}</span>
          </pdl-button>
          <pdl-button qaid="customization-lead-gen-cancel-dialog" secondary @click="handleCancel">
            <span qaid="customization-lead-gen-cancel-dialog-button-text">{{ $t('text.button.cancel') }}</span>
          </pdl-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState, mapActions, mapMutations, mapGetters} from 'vuex';
import {PdlFormGroup} from '@pedal/pdl-form-group';
import {PdlButton} from '@pedal/pdl-button';
import {TrekValidationMixin} from '@/utils/validation/trek-validation-mixin';
import {LocatorApi} from '@/components/locator/mixins/locator-api';
import PaginationControls from '@/components/PaginationControls';
import LocatorStoreList from '@/components/locator/components/LocatorStoreList';
import {UserApi} from '@/api/user';
import LocatorStoreCard from '@/components/locator/components/LocatorStoreCard';
import MapboxAddressSuggestionsInput from '@/components/mapbox/components/MapboxAddressSuggestionsInput';
import {PdlToastType} from '@/constants/pdl-toast-type';
import {delayedScrollToElement} from '@/utils/scroll-to.js';
import {SUBMISSION_STATUS} from '@/familyDetailsPage/store/bike-customization';
import {useIsTranslationLoaded} from '@/composables/is-translation-loaded.js';
import {SUMMARY_DIALOG_VIEWS} from '@/familyDetailsPage/store/bike-customization.js';

export default {
  name: 'CustomizationSubmitToRetailerBody',
  components: {
    PdlFormGroup,
    PdlButton,
    PaginationControls,
    LocatorStoreList,
    LocatorStoreCard,
    MapboxAddressSuggestionsInput,
  },
  mixins: [LocatorApi, TrekValidationMixin],
  setup() {
    return {
      ...TrekValidationMixin.setup(),
    };
  },

  data() {
    return {
      customerEmail: '',
      customerName: '',
      customerPhoneNumber: '',
      retailerLocation: {},
      searchInputError: '',
      preferredContactMethods: [],
      textSearchQuery: '',
      textSearchCenter: [],
      stores: [],
      pagination: {},
      isProjectOneRetailerSearch: true,
      isLoading: false,
      hasSearched: false,
    };
  },

  validations() {
    return {
      customerName: {
        required: this.trekValidators.requiredWithCustomMessage('requestReturnLabel.name.error'),
      },
      customerEmail: {
        required: this.trekValidators.requiredWithCustomMessage('requestReturnLabel.email.error'),
        email: this.trekValidators.email('requestReturnLabel.email'),
      },
      customerPhoneNumber: {
        required: this.trekValidators.requiredWithCustomMessage('contactUs.phone.error'),
        phoneNumber: this.trekValidators.requiredWithCustomMessage('contactUs.phone.error'),
      },
    };
  },

  computed: {
    ...mapGetters('bikeCustomization', ['isBikeEditable']),
    ...mapState('bikeCustomization', ['bikeData', 'hasSavedBuildAndUpdatedRoute', 'isCustomizationSummaryDialogOpen']),
    ...mapState('familyDetails', ['buyZoneId']),
    ...mapState('backend', ['isUserLoggedIn', 'locale']),

    selectedContactMethod() {
      return this.preferredContactMethods.find(({checked}) => checked === true);
    },
    totalPages() {
      if (!this.pagination.pageSize) {
        return 1;
      }
      return Math.ceil(this.pagination.totalResults / this.pagination.pageSize);
    },
    isSubmittable() {
      return !!(
        this.customerEmail &&
        this.customerPhoneNumber &&
        this.selectedContactMethod?.value &&
        this.retailerLocation.id &&
        this.isBikeEditable
      );
    },
  },

  created() {
    useIsTranslationLoaded(() => {
      this.initWhenTranslationsReady();
    });

    UserApi.getCustomerData().then((response) => {
      if (response?.data?.data) {
        this.customerName = response.data.data.name;
        this.customerEmail = response.data.data.uid;
      }
    });
  },

  mounted() {
    this.$emit('body-mounted');
  },

  methods: {
    ...mapActions('bikeCustomization', ['submitToRetailer', 'fetchComponents']),
    ...mapMutations('bikeCustomization', [
      'setIsSubmitToRetailerDialogOpen',
      'setIsSaveDialogVisible',
      'setSummaryDialogView',
      'setIsProcessingCustomizationAction',
    ]),

    initWhenTranslationsReady() {
      this.preferredContactMethods = [
        {value: 'email', displayName: this.$t('contactUs.email'), checked: true},
        {value: 'phone', displayName: this.$t('contactUs.phone'), checked: false},
      ];
    },
    closeModal() {
      this.setIsSubmitToRetailerDialogOpen(false);
    },
    async submit() {
      if (this.bikeData?.code && this.bikeData?.description) {
        const response = await this.handleSubmission();

        if (response?.status === SUBMISSION_STATUS.SENT_TO_RETAILER) {
          this.$notify({
            type: PdlToastType.SUCCESS,
            message: this.$t('product.fdp.customizable.config.submitted'),
          });

          // Reset in case user wishes to make another build and submit that by hitting submit to retailer button again
          this.clearRetailer();
          this.customerPhoneNumber = '';

          if (this.isCustomizationSummaryDialogOpen) {
            this.setSummaryDialogView(SUMMARY_DIALOG_VIEWS.BUILD_SUMMARY);
          } else {
            this.setIsSubmitToRetailerDialogOpen(false);
            delayedScrollToElement(`#${this.buyZoneId}`);
          }
        }
      } else {
        this.setIsSaveDialogVisible(true);
      }
    },
    async handleSubmission() {
      const data = {
        dealerId: this.retailerLocation.id,
        contactMethod: this.selectedContactMethod?.value,
        name: this.customerName,
        email: this.customerEmail,
        phone: this.customerPhoneNumber,
      };

      this.setIsProcessingCustomizationAction(true);

      let response;

      try {
        response = await this.submitToRetailer(data);
      } catch (error) {
        console.warn(error);
        this.$notify({
          type: PdlToastType.ERROR,
          message: this.$t('errorMessage.unableToProcess'),
        });
      } finally {
        this.setIsProcessingCustomizationAction(false);
      }

      return response;
    },
    setRetailerLocation(locationId) {
      const storeIndex = this.stores.findIndex((store) => store.id === locationId);
      this.retailerLocation = this.stores[storeIndex];
    },
    clearRetailer() {
      this.retailerLocation = {};
    },
    handleContactMethodChange(event) {
      this.preferredContactMethods.filter((contactMethod) => {
        if (event && event.target && contactMethod.value === event.target.value) {
          contactMethod.checked = event.target.checked;
        } else {
          contactMethod.checked = false;
        }
      });
    },
    search() {
      this.isLoading = true;
      this.loadData();
      if (!this.hasSearched) {
        this.hasSearched = true;
      }
    },
    pageChange(page) {
      this.loadData(page);
    },
    prevPage() {
      if (this.pagination.page > 1) {
        this.pageChange(this.pagination.page - 1);
      }
    },
    nextPage() {
      if (this.pagination.page < this.totalPages) {
        this.pageChange(this.pagination.page + 1);
      }
    },
    respondToInputChanges({inputValue = '', data = {}, isSelecting = false}) {
      this.textSearchQuery = inputValue;
      this.textSearchCenter = data?.center;
      if (isSelecting) {
        this.search();
      }
    },
    handleCancel() {
      if (this.isCustomizationSummaryDialogOpen) {
        this.setSummaryDialogView(SUMMARY_DIALOG_VIEWS.BUILD_SUMMARY);
      } else {
        this.setIsSubmitToRetailerDialogOpen(false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.scrollbar {
  scrollbar-gutter: stable both-edges;
  scrollbar-width: thin;
}
</style>
